import React from "react";
import {App as AntdApp, Button, Card, Image, Input, Modal, Space, Switch, Table, Tag, Typography} from "antd";

import api from "@/api/axios.js";

const {Title, Text} = Typography;

export default function ManageFlyerModal({open, onClose, flyerId, flyerName})
{
	const {message} = AntdApp.useApp();
	const [rows, setRows] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [saving, setSaving] = React.useState({});
	const [dirty, setDirty] = React.useState({});

	React.useEffect(() =>
	{
		if (!open || !flyerId) return;
		(async () =>
		{
			setLoading(true);
			try
			{
				const {data} = await api.get(`/api/flyers/${flyerId}/cards`);
				setRows(Array.isArray(data) ? data : (data?.items || []));
			}
			finally
			{
				setLoading(false);
			}
		})();
	}, [open, flyerId]);

	const fmtPriceOnBlur = (val) =>
	{
		if (val == null) return "";
		let s = String(val).trim().replace(/\./g, ",").replace(/[^0-9,]/g, "");
		if (!s) return "";
		const [int, dec = ""] = s.split(",");
		return int + "," + (dec + "00").slice(0, 2);
	};

	const setField = (id, key, value) =>
	{
		setRows(prev => prev.map(r => r._id === id ? ({...r, ai: {...(r.ai || {}), [key]: value}}) : r));
		setDirty(prev => ({...prev, [id]: {...(prev[id] || {}), [`ai.${key}`]: value}}));
	};

	const saveRow = async (row) =>
	{
		if (!dirty[row._id]) return;
		setSaving(s => ({...s, [row._id]: true}));
		try
		{
			const patch = dirty[row._id];
			const {data} = await api.patch(`/api/cards/${row._id}`, patch);
			setRows(prev => prev.map(r => r._id === row._id ? data : r));
			setDirty(prev =>
			{
				const x = {...prev};
				delete x[row._id];
				return x;
			});
			message.success("Riga salvata");
		}
		catch (e)
		{
			message.error("Errore salvataggio");
		}
		finally
		{
			setSaving(s => ({...s, [row._id]: false}));
		}
	};

	const toggleValidated = async (row, checked) =>
	{
		setSaving(s => ({...s, [row._id]: true}));
		try
		{
			const {data} = await api.patch(`/api/cards/${row._id}`, {status: checked ? "validated" : "new"});
			setRows(prev => prev.map(r => r._id === row._id ? data : r));
		}
		finally
		{
			setSaving(s => ({...s, [row._id]: false}));
		}
	};

	const columns = [
		{
			title: "Anteprima",
			dataIndex: "relPath",
			width: 120,
			render: (rel, record) =>
			{
				const API = (import.meta.env.VITE_API_URL || "").replace(/\/+$/, "");
				const base = `${API}/media/flyers/${String(rel).replace(/^\/+/, "")}`;
				const src = record?.updatedAt ? `${base}?t=${new Date(record.updatedAt).getTime()}` : base;

				return (
					<div className="mg-thumb">
						<Image
							src={src}
							alt=""
							preview={{mask: 'Apri'}}
							wrapperStyle={{width: '100%', height: '100%'}}
							style={{width: '100%', height: '100%', objectFit: 'contain'}}
						/>
					</div>
				);
			}
		},
		{
			title: "Dati estratti (modificabili)",
			render: (_, r) => (
				<div className="mg-grid">
					<div className="mg-col">
						<label>Nome prodotto</label>
						<Input
							size="small"
							value={r.ai?.nome_prodotto || ""}
							onChange={(e) => setField(r._id, "nome_prodotto", e.target.value)}
						/>
						<label>Marca</label>
						<Input
							size="small"
							value={r.ai?.marca || ""}
							onChange={(e) => setField(r._id, "marca", e.target.value)}
						/>
						<label>Descrizione</label>
						<Input.TextArea
							autoSize={{minRows: 4}}
							value={r.ai?.descrizione || ""}
							onChange={(e) => setField(r._id, "descrizione", e.target.value)}
						/>
						<label>Descrizione estesa</label>
						<Input.TextArea
							autoSize={{minRows: 4}}
							value={r.ai?.descrizione_estesa || ""}
							onChange={(e) => setField(r._id, "descrizione_estesa", e.target.value)}
						/>
					</div>

					<div className="mg-col">
						<label>Meccanica</label>
						<Input
							size="small"
							value={r.ai?.meccanica || ""}
							onChange={(e) => setField(r._id, "meccanica", e.target.value)}
						/>
						<label>Sconto</label>
						<Input
							size="small"
							value={r.ai?.sconto || ""}
							onChange={(e) => setField(r._id, "sconto", e.target.value)}
						/>
						<label>Grammatura</label>
						<Input
							size="small"
							value={r.ai?.grammatura || ""}
							onChange={(e) => setField(r._id, "grammatura", e.target.value)}
						/>
						<label>Note</label>
						<Input.TextArea
							autoSize={{minRows: 1, maxRows: 3}}
							value={r.ai?.note || ""}
							onChange={(e) => setField(r._id, "note", e.target.value)}
						/>
					</div>

					<div className="mg-col">
						<label>Prezzo base</label>
						<Input
							size="small"
							value={r.ai?.prezzo_base || ""}
							onChange={(e) => setField(r._id, "prezzo_base", e.target.value)}
							onBlur={(e) => setField(r._id, "prezzo_base", fmtPriceOnBlur(e.target.value))}
							placeholder="0,00"
						/>
						<label>Prezzo iniziale UM</label>
						<Input
							size="small"
							value={r.ai?.prezzo_iniziale_grammatura || ""}
							onChange={(e) => setField(r._id, "prezzo_iniziale_grammatura", e.target.value)}
							onBlur={(e) => setField(r._id, "prezzo_iniziale_grammatura", fmtPriceOnBlur(e.target.value))}
							placeholder="0,00"
						/>
						<label>Prezzo offerta</label>
						<Input
							size="small"
							value={r.ai?.prezzo_offerta || ""}
							onChange={(e) => setField(r._id, "prezzo_offerta", e.target.value)}
							onBlur={(e) => setField(r._id, "prezzo_offerta", fmtPriceOnBlur(e.target.value))}
							placeholder="0,00"
						/>
						<label>Prezzo offerta UM</label>
						<Input
							size="small"
							value={r.ai?.prezzo_offerta_um || ""}
							onChange={(e) => setField(r._id, "prezzo_offerta_um", e.target.value)}
							onBlur={(e) => setField(r._id, "prezzo_offerta_um", fmtPriceOnBlur(e.target.value))}
							placeholder="0,00"
						/>
					</div>
				</div>
			)
		},
		{
			title: "Stato",
			dataIndex: "status",
			width: 140,
			render: (_, r) => (
				<Space direction="vertical" size={4}>
					<Tag color={r.status === "validated" ? "green" : "gold"} bordered={false}>
						{r.status === "validated" ? "Validata" : "Da validare"}
					</Tag>
					<Switch
						checkedChildren="OK"
						unCheckedChildren="TODO"
						size="small"
						checked={r.status === "validated"}
						loading={!!saving[r._id]}
						onChange={(ck) => toggleValidated(r, ck)}
					/>
				</Space>
			)
		},
		{
			title: "Azioni",
			width: 120,
			render: (_, r) => (
				<Space>
					<Button
						type="primary"
						size="small"
						disabled={!dirty[r._id]}
						loading={!!saving[r._id]}
						onClick={() => saveRow(r)}
					>
						Salva
					</Button>
					<Button
						size="small"
						disabled={!dirty[r._id]}
						onClick={() =>
						{
							api.get(`/api/flyers/${flyerId}/cards`).then(({data}) =>
							{
								const list = Array.isArray(data) ? data : (data?.items || []);
								const fresh = list.find(x => x._id === r._id);
								if (fresh)
								{
									setRows(prev => prev.map(p => p._id === r._id ? fresh : p));
									setDirty(prev =>
									{
										const x = {...prev};
										delete x[r._id];
										return x;
									});
								}
							});
						}}
					>
						Reset
					</Button>
				</Space>
			)
		}
	];

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			width="90vw"
			centered
			destroyOnHidden
			title={<span>Gestisci volantino · <b>{flyerName}</b></span>}
			className="sis-modal"
			styles={{
				content: {
					height: '95vh',
					display: 'flex',
					flexDirection: 'column'
				},
				header: {padding: '12px 16px'},
				body: {
					flex: 1,
					overflow: 'auto',
					padding: 16
				}
			}}
		>
			<Card className="card-elev" style={{marginBottom: 12}}>
				<Space direction={"vertical"} size={4}>
					<Title level={4} style={{margin: 0}}>Cards estratte</Title>
					<Text className="vp-sub">Modifica i campi e salva riga per riga</Text>
				</Space>
			</Card>

			<Table
				rowKey={(r) => r._id}
				columns={columns}
				dataSource={rows}
				loading={loading}
				pagination={{pageSize: 8}}
				size="middle"
				className="mg-table wow-table-card"
			/>
		</Modal>
	);
}
